import { checkHostPattern } from './permission_gate.ts';

/**
 * The driver manifest, `driver.json` next to the driver's bundled module (proposal §6.3). The
 * permissions are what the user consents to when installing a driver, and all the permission gate
 * lets the driver reach. Credentials are in the driver's configuration (decision §12), so the gate
 * is what keeps a driver from sending them anywhere else.
 *
 * ```json
 * {
 *   "id": "json-http",
 *   "name": "JSON over HTTP",
 *   "version": "1.0.0",
 *   "entry": "driver.js",
 *   "permissions": { "configHosts": ["url"] },
 *   "configSchema": { "type": "object", "properties": { "url": { "type": "string" } } }
 * }
 * ```
 */

export interface DriverPermissions {
    /**
     * Hosts the driver may always reach: names (`api.example.com`), subdomains (`*.example.com`,
     * which does not include `example.com` itself), IPv4 addresses and ranges (`192.168.1.0/24`),
     * and IPv6 addresses. Any port.
     */
    readonly hosts: readonly string[];
    /**
     * Configuration fields that name a host the driver may reach, as a host name, `host:port` or a
     * URL (a list of those is fine too). This is how a driver for a device on the LAN is limited to
     * that device: the user names it in the configuration.
     */
    readonly configHosts: readonly string[];
    /** WebSocket connections to the allowed hosts. HTTP to them is always allowed. */
    readonly ws: boolean;
    /** Raw TCP connections to the allowed hosts. */
    readonly tcp: boolean;
}

export interface DriverManifest {
    /** Lower-case letters, digits and `-`; what a connection's `driver` names. */
    readonly id: string;
    readonly name: string;
    /** `major.minor.patch`, optionally with a pre-release or build suffix. */
    readonly version: string;
    readonly description?: string;
    /** The bundled ES module next to the manifest; its default export creates the driver. */
    readonly entry: string;
    readonly permissions: DriverPermissions;
    /** JSON Schema of the driver's configuration; a property with `"secret": true` holds a credential. */
    readonly configSchema?: Readonly<Record<string, unknown>>;
    /**
     * JSON Schema of a data source's parameters, in the order they are chosen, each with a `title`
     * and a `description`; `"x-choices": true` on those whose values `Driver.choices` lists (M-1).
     */
    readonly sourceSchema?: Readonly<Record<string, unknown>>;
    /**
     * How the app finds the driver's systems on the LAN (M-8): the mDNS service type they announce,
     * and the configuration field an address found fills. The app browses; the driver never does.
     */
    readonly discovery?: DriverDiscovery;
}

export interface DriverDiscovery {
    /** A DNS-SD service type: `_hwenergy._tcp`. */
    readonly mdns: string;
    /** The configuration field that takes the address found: `host`. */
    readonly configField: string;
}

/** A DNS-SD service type, as the bridge's `net.browse` takes it. */
const SERVICE_TYPE_PATTERN = /^_[A-Za-z0-9-]{1,63}\._(?:tcp|udp)$/;

export type ManifestResult = { ok: true; manifest: DriverManifest } | { ok: false; issues: string[] };

const ID_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?$/;
const VERSION_PATTERN = /^\d+\.\d+\.\d+(?:[-+][0-9A-Za-z.+-]+)?$/;
const ENTRY_PATTERN = /^[A-Za-z0-9_-][A-Za-z0-9._-]*\.m?js$/;
const CONFIG_KEY_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

function isRecord(value: unknown) : value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function stringList(value: unknown, field: string, issues: string[]) : string[] {
    if (value === undefined) {
        return [];
    }
    if (!Array.isArray(value) || value.some((item) => typeof item !== 'string')) {
        issues.push(`${field}: a list of strings`);

        return [];
    }

    return value as string[];
}

function flag(value: unknown, field: string, issues: string[]) : boolean {
    if (value === undefined) {
        return false;
    }
    if (typeof value !== 'boolean') {
        issues.push(`${field}: true or false`);

        return false;
    }

    return value;
}

/** Checks parsed `driver.json` contents; absent permissions default to none. */
export function parseManifest(value: unknown) : ManifestResult {
    if (!isRecord(value)) {
        return { ok: false, issues: ['the manifest is not a JSON object'] };
    }

    const issues: string[] = [];
    const text = (field: string, pattern?: RegExp, hint?: string) : string => {
        const item = value[field];
        if (typeof item !== 'string' || item.trim() === '' || item.length > 200) {
            issues.push(`${field}: a text of at most 200 characters`);

            return '';
        }
        if (pattern && !pattern.test(item)) {
            issues.push(`${field}: ${hint}`);
        }

        return item;
    };

    const id = text('id', ID_PATTERN, 'lower-case letters, digits and -, at most 64');
    const name = text('name');
    const version = text('version', VERSION_PATTERN, 'a version like 1.0.0');
    const entry = value.entry === undefined ? 'driver.js' : text('entry', ENTRY_PATTERN, 'a .js file next to the manifest');
    const description = typeof value.description === 'string' ? value.description : undefined;

    const rawPermissions = value.permissions ?? {};
    if (!isRecord(rawPermissions)) {
        issues.push('permissions: an object');
    }
    const permissionsSource = isRecord(rawPermissions) ? rawPermissions : {};
    const hosts = stringList(permissionsSource.hosts, 'permissions.hosts', issues);
    for (const pattern of hosts) {
        const problem = checkHostPattern(pattern);
        if (problem) {
            issues.push(`permissions.hosts: "${pattern}" ${problem}`);
        }
    }
    const configHosts = stringList(permissionsSource.configHosts, 'permissions.configHosts', issues);
    for (const key of configHosts) {
        if (!CONFIG_KEY_PATTERN.test(key)) {
            issues.push(`permissions.configHosts: "${key}" is not a configuration field name`);
        }
    }
    for (const key of Object.keys(permissionsSource)) {
        if (!['hosts', 'configHosts', 'ws', 'tcp'].includes(key)) {
            issues.push(`permissions.${key}: not a known permission`);
        }
    }
    const permissions: DriverPermissions = {
        hosts,
        configHosts,
        ws: flag(permissionsSource.ws, 'permissions.ws', issues),
        tcp: flag(permissionsSource.tcp, 'permissions.tcp', issues),
    };

    if (value.sourceSchema !== undefined && !isRecord(value.sourceSchema)) {
        issues.push('sourceSchema: a JSON Schema object');
    }
    const discovery = value.discovery;
    const isDiscovery = isRecord(discovery) && typeof discovery.mdns === 'string' && SERVICE_TYPE_PATTERN.test(discovery.mdns) && typeof discovery.configField === 'string' && CONFIG_KEY_PATTERN.test(discovery.configField);
    if (discovery !== undefined && !isDiscovery) {
        issues.push('discovery: { "mdns": a service type such as "_hwenergy._tcp", "configField": the configuration field it fills }');
    }
    if (value.configSchema !== undefined && !isRecord(value.configSchema)) {
        issues.push('configSchema: a JSON Schema object');
    }
    if (issues.length > 0) {
        return { ok: false, issues };
    }

    return {
        ok: true,
        manifest: {
            id,
            name,
            version,
            entry,
            permissions,
            ...(description !== undefined ? { description } : {}),
            ...(isRecord(value.configSchema) ? { configSchema: value.configSchema } : {}),
            ...(isRecord(value.sourceSchema) ? { sourceSchema: value.sourceSchema } : {}),
            ...(isDiscovery ? { discovery: { mdns: String(discovery.mdns), configField: String(discovery.configField) } } : {}),
        },
    };
}

/** The configuration fields a manifest marks as credentials (`"secret": true`), for redaction. */
export function manifestSecretKeys(manifest: DriverManifest) : string[] {
    const properties = manifest.configSchema?.properties;
    if (!isRecord(properties)) {
        return [];
    }

    return Object.entries(properties).flatMap(([key, schema]) => (isRecord(schema) && schema.secret === true ? [key] : []));
}

/** Whether two permission sets allow the same; a reinstall with different ones needs consent again. */
export function samePermissions(a: DriverPermissions, b: DriverPermissions) : boolean {
    const sameList = (x: readonly string[], y: readonly string[]) : boolean => x.length === y.length && [...x].sort().join('\n') === [...y].sort().join('\n');

    return sameList(a.hosts, b.hosts) && sameList(a.configHosts, b.configHosts) && a.ws === b.ws && a.tcp === b.tcp;
}
